import { ScheduleStatus } from "model/Schedule";
import {
  getBlockStatusStyling,
  getBlockStatusText,
} from "web-apps/company/components/schedule/utils/schedule.status";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import {
  Block,
  ContentSurface,
  StatusText,
  ShiftTypeBadge,
  TimeText,
  TooltipContent,
  TooltipDate,
  TooltipStatus,
  TooltipTime,
  TooltipWorker,
} from "./shiftBlock.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  formatDayMonthLabel,
  formatShiftTimeRange,
} from "web-apps/company/components/schedule/utils/schedule.dates";

type ShiftBlockSize = "small" | "large" | "pill";

interface Props {
  startTime: string;
  endTime: string;
  status: ScheduleStatus;
  size: ShiftBlockSize;
  onClick?: () => void;
  showTypeIcon?: boolean;
  statusLabelOverrides?: Partial<Record<ScheduleStatus, string>>;
  workerName?: string | null;
}

export const ShiftBlock: React.FC<Props> = (props) => {
  const { t, i18n } = useTranslation();
  const {
    startTime,
    endTime,
    status,
    size,
    onClick,
    showTypeIcon,
    statusLabelOverrides,
  } = props;
  const resolvedStatus = status;
  const resolvedWorkerName =
    props.workerName && props.workerName.trim().length > 0
      ? props.workerName.trim()
      : null;
  const styles = getBlockStatusStyling(resolvedStatus);
  const overrideLabel = statusLabelOverrides?.[resolvedStatus];

  const label =
    overrideLabel ??
    (resolvedStatus === ScheduleStatus.PLANNED_ABSENCE ||
    resolvedStatus === ScheduleStatus.UNPLANNED_ABSENCE
      ? t("Schedule.Absence")
      : getBlockStatusText(resolvedStatus, t));

  const isClickable = Boolean(onClick);
  const isPill = size === "pill";
  const showTypeBadge = Boolean(
    showTypeIcon && (size === "small" || size === "large"),
  );
  const hasWorkerTooltip = Boolean(resolvedWorkerName);
  const showStripes = Boolean(styles.isStriped);

  const dateLabel = useMemo(
    () => formatDayMonthLabel(startTime, i18n.language),
    [i18n.language, startTime],
  );

  const timeRange = useMemo(
    () => formatShiftTimeRange(status, startTime, endTime, i18n.language, t),
    [endTime, i18n.language, startTime, status, t],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  const content = (
    <Block
      $size={size}
      $backgroundColor={styles.backgroundColor}
      $textColor={styles.color}
      $clickable={isClickable}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {showTypeBadge && (
        <ShiftTypeBadge>
          <CustomIcon name="v2/shift" size="18px" color={ColorV2.Blue3} />
        </ShiftTypeBadge>
      )}
      <ContentSurface
        $size={size}
        $striped={showStripes}
        $baseColor={styles.backgroundColor}
      >
        {!isPill && <TimeText>{timeRange}</TimeText>}
        {size === "large" && <StatusText>{label}</StatusText>}
      </ContentSurface>
    </Block>
  );

  if (isPill) {
    return (
      <TooltipHover
        content={
          <TooltipContent>
            <TooltipDate>{dateLabel}</TooltipDate>
            <TooltipTime>{timeRange}</TooltipTime>
            {resolvedWorkerName && (
              <TooltipWorker>{resolvedWorkerName}</TooltipWorker>
            )}
            <TooltipStatus>{label}</TooltipStatus>
          </TooltipContent>
        }
        backgroundColor={styles.backgroundColor}
        textColor={styles.color}
        arrow="down"
        textAlign="center"
      >
        {content}
      </TooltipHover>
    );
  }

  if (hasWorkerTooltip) {
    return (
      <TooltipHover
        text={resolvedWorkerName ?? ""}
        backgroundColor={styles.backgroundColor}
        textColor={styles.color}
        arrow="down"
        textAlign="center"
        fullWidth
      >
        {content}
      </TooltipHover>
    );
  }

  return content;
};
